const numberFields = new Set([
  'sellingPrice',
  'quantity',
  'costOfGoods',
  'sellerDiscount',
  'sellerVoucher',
  'packingCost',
  'targetProfit',
  'affiliateRate',
  'adsRate',
]);



function Field({ label, children }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
      />
    </label>
  );
}

export default function CalculatorForm({ formData, onFieldChange, onReset, rules }) {
  const marketplaceRules = rules.marketplaces[formData.marketplace];
  const marketplaceOptions = Object.entries(rules.marketplaces);
  const categoryOptions = Object.entries(rules.categories);
  const showShopeePrograms = formData.marketplace === 'shopee';
  const showPerformanceFields = Boolean(marketplaceRules?.affiliate?.enabled || marketplaceRules?.ads?.enabled);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    onFieldChange(field, numberFields.has(field) ? Number(value) : value);
  };

  return (
    <form className="panel-card p-5 sm:p-6" onSubmit={(event) => event.preventDefault()}>
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-normal text-slate-950">Input Kalkulator</h2>
          <p className="mt-1 text-sm text-slate-500">Nilai diskon, voucher, dan packing dihitung per pesanan.</p>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
        >
          Reset
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="Marketplace">
          <select className="field-input" value={formData.marketplace} onChange={handleChange('marketplace')}>
            {marketplaceOptions.map(([value, marketplace]) => (
              <option key={value} value={value}>
                {marketplace.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Kategori">
          <select className="field-input" value={formData.category} onChange={handleChange('category')}>
            {categoryOptions.map(([value, category]) => (
              <option key={value} value={value}>
                {category.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Nama Produk">
          <input
            className="field-input"
            value={formData.productName}
            onChange={handleChange('productName')}
            placeholder="Contoh: Serum Niacinamide"
          />
        </Field>

        <Field label="SKU">
          <input className="field-input" value={formData.sku} onChange={handleChange('sku')} placeholder="SKU-001" />
        </Field>

        <Field label="Harga Jual / Unit">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.sellingPrice}
            onChange={handleChange('sellingPrice')}
          />
        </Field>

        <Field label="Qty">
          <input
            className="field-input"
            type="number"
            min="1"
            step="1"
            value={formData.quantity}
            onChange={handleChange('quantity')}
          />
        </Field>

        <Field label="HPP / Unit">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.costOfGoods}
            onChange={handleChange('costOfGoods')}
          />
        </Field>

        <Field label="Biaya Packing / Pesanan">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.packingCost}
            onChange={handleChange('packingCost')}
          />
        </Field>

        <Field label="Diskon Seller">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.sellerDiscount}
            onChange={handleChange('sellerDiscount')}
          />
        </Field>

        <Field label="Voucher Seller">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.sellerVoucher}
            onChange={handleChange('sellerVoucher')}
          />
        </Field>

        <Field label="Target Profit Bersih">
          <input
            className="field-input"
            type="number"
            min="0"
            value={formData.targetProfit}
            onChange={handleChange('targetProfit')}
          />
        </Field>
      </div>

      {showShopeePrograms && (
        <div className="mt-6 border-t border-slate-200 pt-5">
          <h3 className="text-base font-semibold text-slate-950">Program Shopee</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Toggle
              label={marketplaceRules.programs.freeShippingXtra.label}
              checked={formData.freeShippingXtra}
              onChange={(checked) => onFieldChange('freeShippingXtra', checked)}
            />
            <Toggle
              label={marketplaceRules.programs.promoXtra.label}
              checked={formData.promoXtra}
              onChange={(checked) => onFieldChange('promoXtra', checked)}
            />
            <Toggle
              label={marketplaceRules.programs.preOrder.label}
              checked={formData.preOrder}
              onChange={(checked) => onFieldChange('preOrder', checked)}
            />
          </div>
        </div>
      )}

      {showPerformanceFields && (
        <div className="mt-6 border-t border-slate-200 pt-5">
          <h3 className="text-base font-semibold text-slate-950">Biaya Performa {marketplaceRules.label}</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Affiliate (%)">
              <input
                className="field-input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.affiliateRate}
                onChange={handleChange('affiliateRate')}
              />
            </Field>
            <Field label="Iklan (%)">
              <input
                className="field-input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.adsRate}
                onChange={handleChange('adsRate')}
              />
            </Field>
          </div>
          {marketplaceRules.programs?.platformCampaign && (
            <div className="mt-4">
              <Toggle
                label={marketplaceRules.programs.platformCampaign.label}
                checked={formData.platformCampaign}
                onChange={(checked) => onFieldChange('platformCampaign', checked)}
              />
            </div>
          )}
        </div>
      )}
    </form>
  );
}
